import React, { ReactElement } from "react";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AvatarProfile } from "@performyard/py-components";

// config
import Config from "./Config";
// components
import QueryButton from "./QueryButton";

// schema
import { FieldFilter } from "../../schemas/report";

// store
import useQueryBuilderStore, { QueryBuilderStore } from "./store";

// utils
import get from "../../../../utils/GetNested";

// css
import css from "./QueryBuilder.module.scss";

/**
 * Used to get the display value for a filter value
 */
const getValue = (item: FieldFilter, handleError: (error: Error) => void): (ReactElement | string)[] | null => {
	try {
		const valueLabelMaps = (useQueryBuilderStore.getState() as QueryBuilderStore).valueLabelMaps;
		const returnables: (string | ReactElement)[] = [];
		switch (item.type) {
			case "department":
				if (item.value) {
					returnables.push(get(valueLabelMaps.groups, item.value, ""));
					returnables.push(get(valueLabelMaps.groups, item.value, ""));
				}
				break;
			case "role":
				if (item.value) {
					returnables.push(get(valueLabelMaps.roles, item.value, ""));
				}
				break;
			case "template":
				if (item.value) {
					returnables.push(get(valueLabelMaps.reviewTemplates, item.value, ""));
					returnables.push(get(valueLabelMaps.reviewTemplates, item.value, ""));
				}
				break;
			case "cycle":
				if (item.value || item.value === null) {
					if (item.value === null) {
						returnables.push("No Review Cycle");
					} else {
						returnables.push(get(valueLabelMaps.reviewCycles, item.value, ""));
					}
				}
				break;
			case "date":
				if (item.value && (typeof item.value === "string" || item.value instanceof Date)) {
					returnables.push(<span>{item.value === "" ? "" : new Date(item.value).toLocaleDateString()}</span>);
					returnables.push(item.value === "" ? "" : new Date(item.value).toLocaleDateString());
				}
				break;
			case "person":
			case "manager":
				if (item.value) {
					const id = get(item, ["value"], "");
					const name = get(valueLabelMaps.people, id, "");
					returnables.push(
						<div>
							<AvatarProfile employeeId={id} employeeName={name} noLink />
						</div>
					);
					returnables.push(name);
				}
				break;
			case "people":
				if (item.value && Array.isArray(item.value)) {
					returnables.push(
						<div className={css.peopleContainer}>
							{item.value.map((person) => {
								const id = person;
								const name = valueLabelMaps.people[person];
								return <AvatarProfile key={id} employeeId={id} employeeName={name} noLink />;
							})}
						</div>
					);
					returnables.push(item.value.map((person) => valueLabelMaps.people[person]).join(", "));
				}
				break;
			case "multi-select":
				if (item.value && Array.isArray(item.value)) {
					returnables.push(<div className={css.peopleContainer}>{item.value.join(", ")}</div>);
					returnables.push(item.value.join(", "));
				}
				break;

			case "boolean":
				if (typeof item.value === "boolean" || (item.value && typeof item.value === "string")) {
					const truthMap = {
						true: "Yes",
						false: "No",
					};
					returnables.push(<div>{truthMap[item.value.toString()]}</div>);
					returnables.push(truthMap[item.value.toString()]);
				}
				break;
			case "status":
				if (item.value && typeof item.value === "string") {
					const label =
						Config.categoryOptions
							?.find((cat) => cat.value === item.category)
							?.statuses?.find((opt) => opt.value === item.value)?.label || "Unknown Status";

					returnables.push(<div>{label}</div>);
					returnables.push(label);
				}
				break;
			case "goal":
				if (item.value && typeof item.value === "string") {
					const label = valueLabelMaps[item.type][item.value];
					returnables.push(<div>{label}</div>);
					returnables.push(label);
				}
				break;
			case "goalOutlook":
				if (item.value && typeof item.value === "string") {
					const outlook: { color: string; label: string; id: string } =
						valueLabelMaps.goalOutlook[item.value];
					returnables.push(
						<div className="flex items-center gap-x-1">
							<div style={{ backgroundColor: outlook.color }} className="h-3 w-3 shrink-0 rounded-full" />
							<div>{outlook.label}</div>
						</div>
					);
					returnables.push(outlook.label);
				}
				break;
			case "goalCategories":
				if (item.value && typeof item.value === "string") {
					const name = valueLabelMaps.goalCategories[item.value];
					returnables.push(<div>{name}</div>);
					returnables.push(name);
				}
				break;
			case "goalTransparency":
				if (["public", "private"].includes(item.value?.toString().toLowerCase() || "")) {
					const transparencies = {
						public: "Public",
						private: "Private",
					};
					returnables.push(<div>{transparencies[item.value?.toString().toLowerCase() || ""]}</div>);
					returnables.push(transparencies[item.value?.toString().toLowerCase() || ""]);
				}
				break;
			default:
				returnables.push(<span>{item.value as string}</span>);
				returnables.push(item.value as string);
		}
		return returnables;
	} catch (error) {
		if (error instanceof Error) handleError(error);
		return null;
	}
};

interface BaseProps {
	removeFilter: (groupIndex: number) => void;
	disabledForManagers: boolean;
}

const Summary = (props: BaseProps) => {
	const { removeFilter, disabledForManagers } = props;
	const [filterUpdate, selectedFilterGroup, handleSelectFilterGroup] = useQueryBuilderStore((state) => [
		state.filterUpdate,
		state.selectedFilterGroup,
		state.handleSelectFilterGroup,
	]);

	// will execute Error Boundary if error occurs
	const handleError = (error) => {
		throw Error(error);
	};

	const getFieldDisplayName = (field: string, filterGroupIndex: number, filterIndex: number) => {
		let fieldName: string | undefined;
		const category = filterUpdate[filterGroupIndex][filterIndex].category;
		if (category === "EMPLOYEE_FIELD") {
			fieldName = field;
		} else {
			fieldName = Config.categoryOptions
				?.find((cat) => cat.value === category)
				?.fields?.find((fld) => fld._id === field)?.name;
		}
		return (
			<div title={fieldName} className={css.fieldName}>
				<span>{fieldName}</span>
			</div>
		);
	};

	return (
		<>
			{filterUpdate.map((filterGroup, filterGroupIndex) => {
				if (filterGroup.length && filterGroup[0].field)
					return (
						<div data-testid="additional-filter-summary" key={`filter ${filterGroupIndex}`}>
							<div
								className={`${css.conditionsWrapper} ${
									selectedFilterGroup === filterGroupIndex ? css.selectedQuerySummary : null
								}`}
								onClick={() => handleSelectFilterGroup(filterGroupIndex)}
							>
								{filterGroup.map((filter, filterIndex) => {
									const category = filterUpdate[filterGroupIndex][filterIndex].category;
									const fieldCategory = category
										? Config.categoryOptions.find((cat) => cat.value === category)?.label
										: null;

									const value = getValue(filter, handleError);

									if (!value) return null;

									const [fieldValue, stringValue] = value;
									return (
										<div key={`filterItem${filterIndex}`} className={css.conditionWrapper}>
											{filterIndex === 0 ? (
												<div className={css.fieldHeader}>
													<span>{fieldCategory}</span>
													{!disabledForManagers && (
														<div
															data-testid="remove-button"
															className={css.removeIcon}
															onClick={() => removeFilter(filterGroupIndex)}
														>
															<FontAwesomeIcon icon={faTimes} />
														</div>
													)}
												</div>
											) : null}
											<div className={css.querySummary}>
												<div className={css.summaryItem}>
													{getFieldDisplayName(filter.field, filterGroupIndex, filterIndex)}
												</div>
												{filter.comparison ? (
													<div className={`${css.summaryItem} ${css.summaryComparison}`}>
														{Config.comparisonValueLookup[filter.comparison]}
													</div>
												) : null}

												<div
													title={stringValue as string}
													className={`${css.summaryItem} ${css.summaryValue}`}
												>
													{fieldValue}
												</div>
												{}
											</div>
										</div>
									);
								})}
							</div>
							{filterGroupIndex !== filterUpdate.length - 1 ? (
								<div className={css.conditionConnector}>
									<QueryButton text="AND" isStatic={true} />
								</div>
							) : null}
						</div>
					);
				return null;
			})}
		</>
	);
};

export default Summary;
